import React from "react";
import BaseTooltip from "../../components/ui/BaseTooltip";

/**
 * Shared frame for every dashboard panel. Matches the Card/panel chrome used
 * across the Work Order dashboard (rounded-2xl, gray-100 border, p-5) and adds
 * the loading / error / empty states so each chart only describes its series.
 */
const ChartPanel = ({
  title,
  subtitle,
  icon,
  accent = "#0F40AE",
  accentBg = "#EEF2FF",
  help,
  isLoading,
  error,
  isEmpty,
  emptyMessage = "No data for this period.",
  children,
  className = "",
}) => (
  <div
    className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col h-full ${className}`}
  >
    <div className="flex items-center gap-3 mb-4">
      {icon && (
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: accentBg }}
        >
          <i className={`${icon} text-sm`} style={{ color: accent }} />
        </div>
      )}
      <div className="min-w-0">
        <h4 className="text-sm font-semibold text-gray-700 truncate">{title}</h4>
        {subtitle && <p className="text-[11px] text-gray-400 mt-0.5 truncate">{subtitle}</p>}
      </div>
      {help && (
        <BaseTooltip content={<p className="text-white text-[11px] leading-relaxed">{help}</p>}>
          <i className="fa-light fa-circle-info text-gray-300 hover:text-gray-500 cursor-help text-sm ml-auto shrink-0 transition-colors" />
        </BaseTooltip>
      )}
    </div>

    {isLoading ? (
      <div className="flex items-center justify-center flex-1 min-h-[280px]">
        <i className="fa-solid fa-circle-notch fa-spin text-primary text-lg" />
      </div>
    ) : error ? (
      <div className="flex flex-col items-center justify-center flex-1 min-h-[280px] text-center px-4">
        <i className="fa-solid fa-triangle-exclamation text-2xl text-red-400 mb-2" />
        <p className="text-sm text-gray-500">Couldn’t load this chart.</p>
        <p className="text-[11px] text-gray-400 mt-1">Please refresh and try again.</p>
      </div>
    ) : isEmpty ? (
      <div className="flex flex-col items-center justify-center flex-1 min-h-[280px] text-center px-4">
        <i className="fa-light fa-chart-simple text-2xl text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      </div>
    ) : (
      <div className="flex-1">{children}</div>
    )}
  </div>
);

export default ChartPanel;
